/** Pure derivations for the WhatsApp inbox (unit tested): list tabs and the lead qualification rows. Only API fields are used. */
export const INBOX_TABS = ['open', 'ai', 'closed'] as const;
export type InboxTab = (typeof INBOX_TABS)[number];
export type ConvLike = Record<string, unknown>;

/** Open = anything not closed; AI = the assistant is replying; Closed = closed. (No snooze state exists in the API, so there is no Snoozed tab.) */
export function inTab(c: ConvLike, tab: InboxTab): boolean {
  const state = String(c.state ?? '');
  return tab === 'open' ? state !== 'closed' : tab === 'ai' ? state === 'ai_active' : state === 'closed';
}
export const tabCounts = (rows: readonly ConvLike[]): Record<InboxTab, number> => ({
  open: rows.filter((r) => inTab(r, 'open')).length, ai: rows.filter((r) => inTab(r, 'ai')).length, closed: rows.filter((r) => inTab(r, 'closed')).length,
});

/** The five qualification questions of a WhatsApp lead, in the order the API captures them. */
export const QUALIFICATION_FIELDS = ['name', 'email', 'service', 'budget', 'timeline'] as const;
export interface QualRow { key: (typeof QUALIFICATION_FIELDS)[number]; value: string | null }
/** One row per qualification field; a field that was not captured has value null (shown as "Not captured", never guessed). */
export function qualificationRows(fields: Record<string, unknown> | null | undefined): QualRow[] {
  return QUALIFICATION_FIELDS.map((key) => {
    const v = fields?.[key];
    return { key, value: typeof v === 'string' && v.trim() ? v.trim() : null };
  });
}

/** "Return to AI" hands a conversation back to the assistant. Only a conversation a person is handling can be handed back
 * (the API has no snooze state; closed conversations reopen on the next inbound message). */
export const canReturnToAi = (state: string | null | undefined): boolean => state === 'human_active';

/** Inbox-wide AI reply mode (GET/PUT /v1/whatsapp-inbox/settings). The API default is 'approve'. */
export const AI_MODES = ['off', 'approve', 'auto'] as const;
export type AiMode = (typeof AI_MODES)[number];
export function aiModeOf(data: unknown): AiMode {
  const v = data && typeof data === 'object' ? (data as { aiMode?: unknown }).aiMode : undefined;
  return (AI_MODES as readonly unknown[]).includes(v) ? (v as AiMode) : 'approve';
}
/** PUT /settings is owner/admin only; everyone else sees the setting read-only. */
export const canEditAiMode = (roles: readonly string[] | null | undefined): boolean => !!roles?.some((r) => r === 'customer_owner' || r === 'customer_admin');
